import { useCallback, useEffect, useRef, useState, type ClipboardEvent } from 'react';
import { NotificationTextMessage } from '@/core/enums/notification';
import { ExportFormatCode, type AuthorisationCodeRow } from '@/core/models/practice.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { practiceService } from '@/core/services/practice';
import { AUTH_CODE_LENGTH, isAuthCodeInvalid, normalizeAuthCode, sanitizeAuthCode } from '@/core/utils/authCode';
import { Button } from '@/kit/Button';
import { ClosePopup } from '@/kit/ClosePopup';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { openDialog } from '@/kit/dialog';
import { InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { PageHeader, PageHeaderFooter } from '@/kit/PageHeader';
import { Select, SelectOption } from '@/kit/Select';
import { ExportMenu } from './ExportMenu';
import { SearchBox } from './SearchBox';
import { useDebouncedSearch } from './useDebouncedSearch';
import './AuthorisationCodes.scss';

/**
 * Companies House authentication codes of the firm's limited companies and LLPs (BUG-058, as the 3.0 screen): the grid is
 * read-only until Edit, then Save stores every complete code (a code that is not exactly 6 letters/numbers highlights its
 * row and is not saved) and Cancel throws every edit away. A box takes 6 characters at most and nothing is cut silently.
 */
export function AuthorisationCodes() {
  const [dataSource, setDataSource] = useState<AuthorisationCodeRow[]>([]);
  /**
   * What the select shows. Angular's `[ngModel]="filter"` only rewrites the select when the bound value CHANGES, so
   * this can differ from the filter that is sent (`filterRef`): clearing while "All" is selected shows blank.
   * '0' is the "All" option, so the filter opens on a selected value rather than blank.
   */
  const [filter, setFilter] = useState<string | null>('0');
  const [editing, setEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  /** Rows whose typed code is not a complete code after a Save: highlighted, kept in the box, not saved. */
  const [invalidIds, setInvalidIds] = useState<Set<string>>(new Set());

  // The private Angular fields, mirrored in refs so every handler reads the latest value.
  const search = useRef('');
  const filterRef = useRef('0');
  /** The code each row had when Edit was pressed (or last saved): what Cancel restores and Save compares with. */
  const originalCodes = useRef(new Map<string, string | null>());

  const setCode = (id: string, authCode: string | null) =>
    setDataSource((rows) => rows.map((r) => (r.universalId === id ? { ...r, authCode } : r)));

  const load = useCallback(() => {
    practiceService.getAuthorisationCodes(search.current, Number(filterRef.current ?? 0)).then(
      (response) => {
        setDataSource(response.items);
        setInvalidIds(new Set());
      },
      (error) => notifyError(error),
    );
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onSearch = useDebouncedSearch((text) => {
    search.current = text;
    load();
  });

  const onFilterChange = (value: string | null) => {
    // Clearing the select falls back to "All" for the request. The select itself is only rewritten to "All" when
    // that is a change of the bound value; clearing while it already shows "All" leaves it blank (Angular quirk).
    const next = value ?? '0';
    const changed = next !== filterRef.current;
    filterRef.current = next;
    setFilter(changed ? next : value);
    // Reloading replaces the rows, so an edit in progress ends with it (as the old grid did).
    setEditing(false);
    load();
  };

  const startEdit = () => {
    originalCodes.current = new Map(dataSource.map((r) => [r.universalId, r.authCode]));
    setInvalidIds(new Set());
    setEditing(true);
  };

  const cancelEdit = () => {
    setDataSource((rows) => rows.map((r) => ({ ...r, authCode: originalCodes.current.get(r.universalId) ?? null })));
    setInvalidIds(new Set());
    setEditing(false);
  };

  /** Letters and numbers only, upper-case; the box itself stops at 6 characters. */
  const onCodeInput = (row: AuthorisationCodeRow, value: string) => {
    setCode(row.universalId, value.replace(/[^A-Za-z0-9]/g, '').toUpperCase());
    setInvalidIds((current) => {
      if (!current.has(row.universalId)) return current;
      const next = new Set(current);
      next.delete(row.universalId);
      return next;
    });
  };

  /**
   * A paste longer than 6 characters is cut to 6 IN THE BOX, before anything is saved, so the user sees exactly what
   * Save will store (nothing is cut silently afterwards).
   */
  const onCodePaste = (row: AuthorisationCodeRow, event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const input = event.currentTarget;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? start;
    const pasted = event.clipboardData.getData('text');
    onCodeInput(row, sanitizeAuthCode(input.value.slice(0, start) + pasted + input.value.slice(end)));
  };

  const save = async () => {
    if (isSaving) return;
    const changed = dataSource.filter(
      (row) =>
        normalizeAuthCode(row.authCode) !== normalizeAuthCode(originalCodes.current.get(row.universalId) ?? null),
    );
    const invalid = changed.filter((row) => isAuthCodeInvalid(row.authCode));
    const valid = changed.filter((row) => !isAuthCodeInvalid(row.authCode));
    setInvalidIds(new Set(invalid.map((row) => row.universalId)));

    if (changed.length === 0) {
      // Test case 3195: Save with nothing changed says so instead of silently closing edit mode.
      notifyError('Please enter auth code to update.');
      return;
    }

    setIsSaving(true);
    let failed = 0;
    const saved = await Promise.all(
      valid.map((row) => {
        const code = normalizeAuthCode(row.authCode);
        return practiceService.saveAuthorisationCode(row.universalId, code).then(
          () => {
            originalCodes.current.set(row.universalId, code);
            setCode(row.universalId, code);
            return true;
          },
          (error) => {
            failed += 1;
            notifyError(error);
            return false;
          },
        );
      }),
    );
    setIsSaving(false);

    if (invalid.length > 0) {
      // The saved rows are reported first so the problem rows are what the user is left looking at.
      if (saved.some(Boolean)) notifySuccess('Codes saved successfully.');
      notifyError(
        `${invalid.length} code(s) are not exactly ${AUTH_CODE_LENGTH} letters/numbers, so they were not saved. Correct the highlighted rows.`,
      );
      return;
    }
    if (failed > 0) return;
    if (valid.length > 0) notifySuccess('Codes saved successfully.');
    setEditing(false);
  };

  const clearCode = (row: AuthorisationCodeRow) => {
    if (!row.authCode) {
      return;
    }
    void openDialog(ClosePopup, { data: { itemName: 'Auth. Code', count: 1 } })
      .afterClosed()
      .then((confirmed) => {
        if (!confirmed) {
          return;
        }
        practiceService.saveAuthorisationCode(row.universalId, null).then(
          () => {
            setCode(row.universalId, null);
            notifySuccess(NotificationTextMessage.recordDeletedSuccessfully);
          },
          (error) => notifyError(error),
        );
      });
  };

  const onExport = (format: ExportFormatCode) => {
    practiceService
      .exportAuthorisationCodes({ format, searchText: search.current, filter: Number(filterRef.current ?? 0) })
      .catch((error) => notifyError(error));
  };

  const columns: DataTableColumn<AuthorisationCodeRow>[] = [
    { id: 'name', header: 'Name', cell: (row) => row.name },
    { id: 'registerno', header: 'Registration No.', cell: (row) => row.registrationNo },
    { id: 'utrno', header: 'UTR No', cell: (row) => row.utrNo },
    {
      id: 'authcode',
      header: 'Auth. Code',
      headerClassName: 'w-300',
      cell: (row) => (
        <div className="mt-5 mb-5">
          <InputField
            fieldClassName="w-150"
            type="text"
            charMode="alphanumeric"
            maxLength={AUTH_CODE_LENGTH}
            placeholder="6 characters"
            autoComplete="off"
            readOnly={!editing}
            invalid={invalidIds.has(row.universalId)}
            value={row.authCode ?? ''}
            onChange={(event) => onCodeInput(row, event.currentTarget.value)}
            onPaste={(event) => onCodePaste(row, event)}
            onKeyDown={(event) => {
              if (event.key === 'Escape' && editing) cancelEdit();
            }}
          />
          {invalidIds.has(row.universalId) && (
            <div className="text-red font-size-12">Must be exactly 6 letters/numbers (e.g. AB1234).</div>
          )}
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Action',
      cell: (row) => (
        <div className="d-flex align-items-center grid-actions">
          <Button title="Delete" disabled={editing} onClick={() => clearCode(row)}>
            <Icon name="delete_outline" outlined />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="sidemenu-hide authorisation-codes-page">
      <div className="d-flex h-100">
        <div className="form wp-100">
          {/* Design review 4 Nov 2026: Edit stays in the sticky page header; once in edit mode, Save/Cancel sit in a
              persistent sticky footer rather than a row under the table or the header itself. This page has no
              left-hand menu of its own (`sidemenu-hide`), so its title is not a duplicate and stays shown. */}
          <PageHeader
            title="Authorisation Codes"
            mode={editing ? 'edit' : 'view'}
            actionsPlacement="footer"
            busy={isSaving}
            onEdit={startEdit}
            onSave={() => void save()}
            onCancel={cancelEdit}
          />
          <div className="content-body">
            <div className="info-message warning-message mb-1">
              Manage company authorisation codes (for all limited type companies).
            </div>
            <div className="content-header mb-0">
              <div className="content-header-left mb-1">
                <SearchBox onSearch={(text) => onSearch(text.trim())} />
              </div>

              <div className="content-header-right mb-1">
                <Select
                  className="w-150"
                  openOnFocus
                  panelClass="myPanelClass"
                  value={filter}
                  onChange={(value) => onFilterChange(value as string | null)}
                >
                  <SelectOption value="0">All</SelectOption>
                  <SelectOption value="1">Pending only</SelectOption>
                  <SelectOption value="2">Entered only</SelectOption>
                </Select>
                <ExportMenu onExport={onExport} />
              </div>
            </div>
            <div className="mat-table-wrapper no-hover">
              <DataTable stickyHeader columns={columns} rows={dataSource} rowKey={(row) => row.universalId} />
            </div>
          </div>
          {editing && (
            <PageHeaderFooter mode="edit" busy={isSaving} onSave={() => void save()} onCancel={cancelEdit} />
          )}
        </div>
      </div>
    </div>
  );
}
